/**
 * Plastic ban drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const plasticBanDrawingsPack: ToilePack = {
    slug: 'plastic-ban-drawings',
    category: 'coloring',
    name: 'Plastic ban drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'banned', label: 'No plastic', subject: 'a plastic bag inside a red circle with a line through it'},
        {key: 'cloth', label: 'Cloth bag', subject: 'a hand passing a cloth bag of vegetables instead of a plastic bag'},
        {key: 'turtle', label: 'Turtle in sea', subject: 'a sea turtle tangled in a plastic bag among fish'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a bottle', 'a beach', 'a shopper'],
    directionHint: 'try: a bottle',
    seo: {
        title: 'Plastic Ban Drawings: No Plastic, Cloth Bag, Turtle',
        metaDescription: 'Nine plastic ban drawings: a crossed-out plastic bag, a cloth bag swap and a turtle tangled in plastic, in three styles.',
        h1: 'Plastic ban drawings: no plastic bag, cloth bag, turtle in plastic',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Plastic ban drawings for a say no to plastic poster or a competition. The set shows a plastic bag inside a red circle with a line through it, a cloth bag of vegetables replacing a plastic bag, and a sea turtle tangled in a plastic bag among fish. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a bottle, a beach or a shopper.',
        useCases: [
            'a say no to plastic poster for a competition',
            'a plastic pollution chart for a class',
            'a classroom display on the environment',
            'a colouring page for a school campaign',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Does the turtle picture show a real sea turtle?', a: 'It is a drawn sea turtle, meant to show how plastic harms sea life.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
